import { Industries, StageOptions } from "@/lib/utils";
import { useWorkspaceStore } from "@/store/workspace-store";
import { Filter, X } from "lucide-react";
import { useMemo, useState } from "react";
import { DateRange } from "react-day-picker";
import { Checkbox } from "../ui/checkbox";
import { DatePickerWithRange } from "../ui/date-picker-with-range";
import { Label } from "../ui/label";
import { MultiSelect } from "../ui/multi-select";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "../ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import type { DealTransformed } from "@/lib/schema/deal-schema";

interface FilterSheetProps {
  onFiltersChange: (filters: FilterValues) => void;
  deals: Record<string, DealTransformed>;
  dealCountByStatus: Record<string, number>;
}

export interface FilterValues {
  industries: string[];
  statuses: string[];
  stages: string[];
  hasPitchDeck: boolean;
  fitScoreBuckets: string[];
  sources: string[];
  dateRange: DateRange | undefined;
}

// Sentinel for filtering deals with no source value (null/empty string).
export const SOURCE_NONE = "__none__";

const FIT_SCORE_BUCKETS = [
  { label: "Strong (70+)", value: "strong" },
  { label: "Medium (40-69)", value: "medium" },
  { label: "Weak (<40)", value: "weak" },
  { label: "Not yet analyzed", value: "unanalyzed" },
];

const EMPTY_FILTERS: FilterValues = {
  industries: [],
  statuses: [],
  stages: [],
  hasPitchDeck: false,
  fitScoreBuckets: [],
  sources: [],
  dateRange: undefined,
};

const countActive = (f: FilterValues): number =>
  f.industries.length +
  f.statuses.length +
  f.stages.length +
  f.fitScoreBuckets.length +
  f.sources.length +
  (f.hasPitchDeck ? 1 : 0) +
  (f.dateRange ? 1 : 0);

export function FilterSheet({
  onFiltersChange,
  deals,
  dealCountByStatus,
}: FilterSheetProps) {
  const { activeWorkspace } = useWorkspaceStore();
  const [filters, setFilters] = useState<FilterValues>(EMPTY_FILTERS);
  const isMobile = useIsMobile();

  const sourceOptions = useMemo(() => {
    const distinct = new Set<string>();
    Object.values(deals).forEach((d) => {
      const s = d.overview.source;
      if (s) distinct.add(s);
    });
    return [
      ...Array.from(distinct)
        .sort()
        .map((s) => ({ label: s, value: s })),
      { label: "(none)", value: SOURCE_NONE },
    ];
  }, [deals]);

  const handleFilterChange = (newFilters: Partial<FilterValues>) => {
    const updated = { ...filters, ...newFilters };
    setFilters(updated);
    onFiltersChange(updated);
  };

  const activeCount = countActive(filters);
  const hasActiveFilters = activeCount > 0;

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    onFiltersChange(EMPTY_FILTERS);
  };

  const triggerClassName = cn(
    "h-8 inline-flex items-center gap-1.5 rounded-md border px-2.5 text-xs transition-colors",
    hasActiveFilters
      ? "border-foreground/20 bg-foreground/[0.06] text-foreground"
      : "border-border bg-background text-foreground hover:bg-foreground/[0.04]"
  );

  const triggerContent = (
    <>
      <Filter className="h-3.5 w-3.5 text-muted-foreground" />
      Filters
      {hasActiveFilters && (
        <span className="ml-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-sm bg-foreground px-1 font-mono text-[10px] tabular-nums text-background">
          {activeCount}
        </span>
      )}
    </>
  );

  const clearButton = hasActiveFilters ? (
    <button
      type="button"
      onClick={clearFilters}
      className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
    >
      <X className="h-3 w-3" />
      Clear
    </button>
  ) : null;

  const filterControls = (
    <div className="space-y-3.5">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">
                Added date
              </Label>
              <DatePickerWithRange
                value={filters.dateRange}
                onChange={(range) => handleFilterChange({ dateRange: range })}
                modalPopover={true}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Fit score</Label>
              <MultiSelect
                id="fit-score-filter"
                options={FIT_SCORE_BUCKETS}
                onValueChange={(values) =>
                  handleFilterChange({ fitScoreBuckets: values })
                }
                value={filters.fitScoreBuckets}
                placeholder="Any fit score"
                modalPopover={true}
                showSearch={false}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Status</Label>
              <MultiSelect
                id="status-filter"
                options={(activeWorkspace?.statuses || []).map((status) => ({
                  label: `${status.title} (${
                    dealCountByStatus[status.title] ?? 0
                  })`,
                  value: status.title,
                  style: status.colour?.startsWith("#")
                    ? { backgroundColor: status.colour, color: "white" }
                    : undefined,
                  className: !status.colour?.startsWith("#")
                    ? status.colour
                    : "",
                }))}
                onValueChange={(values) =>
                  handleFilterChange({ statuses: values })
                }
                value={filters.statuses}
                placeholder="Any status"
                modalPopover={true}
                showSearch={false}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Source</Label>
              <MultiSelect
                id="source-filter"
                options={sourceOptions}
                onValueChange={(values) =>
                  handleFilterChange({ sources: values })
                }
                value={filters.sources}
                placeholder="Any source"
                modalPopover={true}
                showSearch={false}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Stage</Label>
              <MultiSelect
                id="stage-filter"
                options={StageOptions.map((stage) => ({
                  label: stage.label,
                  value: stage.value,
                }))}
                onValueChange={(values) =>
                  handleFilterChange({ stages: values })
                }
                value={filters.stages}
                placeholder="Any stage"
                modalPopover={true}
                showSearch={false}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Industry</Label>
              <MultiSelect
                id="industries-filter"
                options={Industries.map((industry) => ({
                  label: industry,
                  value: industry,
                }))}
                onValueChange={(values) =>
                  handleFilterChange({ industries: values })
                }
                value={filters.industries}
                placeholder="Any industry"
                modalPopover={true}
                showSearch={false}
              />
            </div>

            <label className="flex cursor-pointer items-center gap-2 pt-1">
              <Checkbox
                checked={filters.hasPitchDeck}
                onCheckedChange={(checked) =>
                  handleFilterChange({ hasPitchDeck: !!checked })
                }
                className="h-3.5 w-3.5 border-border data-[state=checked]:bg-foreground data-[state=checked]:border-foreground data-[state=checked]:text-background"
              />
              <span className="text-xs text-foreground">Has pitch deck</span>
            </label>
    </div>
  );

  if (isMobile) {
    return (
      <Sheet>
        <SheetTrigger className={triggerClassName}>{triggerContent}</SheetTrigger>
        <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto p-0">
          <div className="flex items-center justify-between border-b border-border px-4 py-3 pr-12">
            <SheetTitle className="text-sm font-semibold text-foreground">
              Filters
            </SheetTitle>
            {clearButton}
          </div>
          <div className="px-4 py-3">{filterControls}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Popover>
      <PopoverTrigger className={triggerClassName}>{triggerContent}</PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        className="w-[360px] max-w-[calc(100vw-2rem)] p-0"
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/80">
            Filters
          </span>
          {clearButton}
        </div>
        <div className="max-h-[min(560px,calc(100vh-160px))] overflow-y-auto px-4 py-3">
          {filterControls}
        </div>
      </PopoverContent>
    </Popover>
  );
}
